import React from 'react';
import useQuery from '../useQuery';
import {DEFAULT_SERVICE_DESCRIPTION_SEPARATOR} from '../constants';
import getTagValue, {getPosterUrl} from '../utilities/get-tag-value';
import {format} from 'date-fns';
import i18n from '../i18n';

/** Safe inside a script node (avoids breaking on </ in strings). */
const toSafeJsonLd = (obj) => JSON.stringify(obj).replace(/</g, '\\u003c');

const schemaScriptSelector = (scriptType) =>
    `script[data-script-type="${(scriptType)}"]`;

export const useSingleSchemaScript = (jsonLdText, scriptType = 'schema') => {
    React.useEffect(() => {
        const selector = schemaScriptSelector(scriptType);
        const nodes = document.querySelectorAll(selector);
        for (let i = 1; i < nodes.length; i++) {
            nodes[i].remove();
        }
        let script = nodes[0];
        if (!script) {
            script = document.createElement('script');
            script.type = 'application/ld+json';
            script.setAttribute('data-script-type', scriptType);
            document.head.appendChild(script);
        }
        script.textContent = jsonLdText;

        return () => {
            script.remove();
        };
    }, [jsonLdText, scriptType]);
};

export const ServiceSchemaScript = React.memo(({service}) => {
    const {target, endpoints, description} = service;
    const {locale = 'uk'} = useQuery();
    const targetDate = target ? format(new Date(target), "yyyy-MM-dd'T'HH:mm:ssxxx") : null;
    const eventDescription = i18n(service, 'details', locale);
    const eventName = i18n(service, 'name', locale);
    const serviceTags = description ? description.split(DEFAULT_SERVICE_DESCRIPTION_SEPARATOR) : [];
    const posterUrl = getPosterUrl(description);
    const targetEndVal = getTagValue(serviceTags, 'targetEnd');
    const targetEnd = targetEndVal ? format(new Date(targetEndVal), "yyyy-MM-dd'T'HH:mm:ssxxx") : null;
    const endpointList = endpoints ?? [];
    const pricesArray = endpointList.length
        ? endpointList.map(e => e.price).sort((a, b) => a - b)
        : [];
    const lowestTicketPrice = pricesArray.length ? pricesArray[0] : null;
    const highestTicketPrice = pricesArray.length ? pricesArray[pricesArray.length - 1] : null;
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'Event',
        name: eventName,
        startDate: targetDate,
        description: eventDescription,
        eventStatus: 'https://schema.org/EventScheduled',
    };
    if (lowestTicketPrice != null) {
        schema.offers = {
            '@type': 'AggregateOffer',
            price: lowestTicketPrice,
            lowPrice: lowestTicketPrice,
            highPrice: highestTicketPrice,
            priceCurrency: 'UAH',
            availability: 'https://schema.org/InStock',
        };
    }
    if (targetEnd) {
        schema.endDate = targetEnd;
    }
    if (posterUrl) {
        schema.image = [posterUrl];
    }
    useSingleSchemaScript(toSafeJsonLd(schema));
    return null;
});
